import React from 'react';
import {
  ChevronLargeRightIcon,
  HStack,
  MoreIcon,
  PageDefaultIcon,
  PageDraftIcon,
  Text,
  Tooltip,
  TooltipProps,
  View,
} from '@packages/systems/spring';
import {ExtraPageOptionType} from './types';
import {isFolder} from '@packages/shared/utils/PageUtils';

/**
 * Returns the appropriate icon based on the page type.
 */
export const getPageIcon = (pageType: ExtraPageOptionType['pageType']) => {
  switch (pageType) {
    case 'draft':
      return <PageDraftIcon data-automation-id="page-draft-icon" />;
    default:
      return <PageDefaultIcon data-automation-id="page-icon" />;
  }
};

export const getPageAutomationId = (label: string) =>
  `${label
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[\[\]]/g, '')}-page`;

// Selection components
export const PathSegment = ({children}: {children: React.ReactNode}) => (
  <Text
    colorVariant="secondary"
    whiteSpace="nowrap"
    textOverflow="ellipsis"
    overflow="hidden"
  >
    {children}
  </Text>
);

export const StyledMoreIcon = React.forwardRef<HTMLDivElement>((_, ref) => (
  <View
    ref={ref}
    borderRadius="$1"
    backgroundColor="$uiCardBackground"
    width={16}
    height={12}
    display="flex"
    flexDirection="column"
    justifyContent="center"
  >
    <MoreIcon tabIndex={-1} size={16} colorVariant="secondary" />
  </View>
));

export const PagePath = ({path}: {path: string[]}) => (
  <HStack spacing={1} maxWidth={480}>
    {/* if the path is less than or equal to 4, render the full path */}
    {path.length <= 4 ? (
      path.map((segment, index) => (
        <React.Fragment key={index}>
          {index > 0 && <ChevronLargeRightIcon colorVariant="secondary" />}
          <PathSegment>{segment}</PathSegment>
        </React.Fragment>
      ))
    ) : (
      /* otherwise, render the first segment, a more icon, and the last 3 segments */
      <>
        <PathSegment>{path[0]}</PathSegment>
        <ChevronLargeRightIcon colorVariant="secondary" />
        <StyledMoreIcon />
        {path.slice(-3).map((segment, index) => (
          <React.Fragment key={index}>
            <ChevronLargeRightIcon colorVariant="secondary" />
            <PathSegment>{segment}</PathSegment>
          </React.Fragment>
        ))}
      </>
    )}
  </HStack>
);

export const FilePathContent = React.forwardRef(
  ({path}: {path: string[]}, ref: React.ForwardedRef<HTMLDivElement>) => (
    <HStack spacing={0} marginLeft={4} width="fit-content" ref={ref}>
      {path.length > 1 && (
        <>
          <StyledMoreIcon />
          <ChevronLargeRightIcon />
        </>
      )}
      <Text textOverflow="ellipsis" overflow="hidden" marginRight="$0.5">
        {path[path.length - 1]}
      </Text>
    </HStack>
  )
);

export const FilePathTooltip = ({
  path,
  isDisabled,
}: {
  path: string[];
  isDisabled: TooltipProps['isDisabled'];
}) => {
  if (path.length > 1) {
    return (
      <Tooltip asChild isDisabled={isDisabled} label={<PagePath path={path} />}>
        <FilePathContent path={path} />
      </Tooltip>
    );
  }
  return <FilePathContent path={path} />;
};

/**
 * Utility function to determine the type of page based on its properties.
 */
export const getPageType = ({
  isHomePage,
  isDraft,
}: {
  isHomePage?: boolean;
  isDraft?: boolean;
}) => {
  if (isHomePage) {
    return 'homepage';
  }
  if (isDraft) {
    return 'draft';
  }
  return undefined;
};

export const getPagePath = (ancestry: Immutable.List<unknown>) =>
  ancestry
    // Remove the root node and current page from our ancestry.
    .slice(1, -1)
    // Ancestry is ordered from leaf -> root, but the folders is root -> leaf.
    .reverse()
    // Filter out any non-folder nodes.
    .filter((node: unknown) => isFolder(node))
    .map((node: unknown) =>
      (node as {get: (key: string) => string}).get('name')
    )
    .toArray();
